import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';
import ScreenLoader from '../ScreenLoader';

// <Route exact path='/claims'>
//   <RequireFeature featureKey='claims'>
//     <ClaimsScreen />
//   </RequireFeature>
// </Route>
//
// Reads tenantConfig.features from Redux. If the requested feature is
// disabled for the current tenant, holds a lightweight placeholder while
// the config resolves, then either renders children or redirects.
//
// This is a client-side polish guard — the backend must also 403 on the
// underlying API with requireFeature() middleware. This just prevents
// bookmarks / typed URLs from mounting a disabled screen and blanking
// out because the API refuses.
const RequireFeature = ({ featureKey, children, redirectTo = '/dashboard' }) => {
  const features = useSelector(state => state?.tenantConfig?.config?.features) || {};
  const configLoaded = useSelector(state => Boolean(state?.tenantConfig?.config));
  const configErrored = useSelector(state => Boolean(state?.tenantConfig?.error));
  // SaaS God (role='admin') has org=null so tenantConfig never loads —
  // don't gate them. All other roles must wait for a resolved config.
  const userRole = useSelector(state =>
    state?.loggedinUser?.userInfo?.data?.data?.role
    || state?.userLogin?.userInfo?.data?.user?.role
    || null
  );
  const enabled = Boolean(features[featureKey]);
  const isAdmin = userRole === 'admin';
  const isResolved = isAdmin || configLoaded || configErrored;

  useEffect(() => {
    if (isResolved && !isAdmin && !enabled) {
      toast.error('This feature is not enabled for your organisation.');
    }
  }, [isResolved, isAdmin, enabled]);

  // Test Run 7-24: previously we returned <>{children}</> during the
  // pre-hydration window, which meant a disabled route mounted its
  // screen for 1–2s and only redirected once fetchTenantConfig resolved.
  // The screen's own API calls got 403'd in that window, leaving the
  // "This feature is not enabled" toast to fire on top of a blank page.
  // Hold until the config is known.
  //
  // What that hold used to render was an empty <div>. On a hard refresh
  // the config arrives only after /users/get-user and then /tenant/config
  // have both come back, so a gated route showed several seconds of blank
  // white page and looked broken. Render the same loading state the rest
  // of the app uses instead — the wait is unchanged, but it now reads as
  // a wait.
  if (!isResolved) return <ScreenLoader />;
  if (!isAdmin && !enabled) return <Redirect to={redirectTo} />;
  return <>{children}</>;
};

export default RequireFeature;
